import React, { useState } from 'react';
import { Card, Row, Col, Statistic, DatePicker, Space, Table, Select, Progress } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, DollarOutlined } from '@ant-design/icons';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const OnlineReports = () => {
    const { t } = useTranslation();
    const [dateRange, setDateRange] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    // Fetch summary
    const { data: summaryData } = useQuery(['online-reports-summary', dateRange], async () => {
        const params = {};
        if (dateRange && dateRange[0] && dateRange[1]) {
            params.startDate = dateRange[0].toISOString();
            params.endDate = dateRange[1].toISOString();
        }
        const response = await api.get('/online-reports/summary', { params });
        return response.data.data;
    });

    // Fetch income by month
    const { data: incomeByMonth } = useQuery(['online-income-by-month', selectedYear], async () => {
        const response = await api.get('/online-reports/income-by-month', {
            params: { year: selectedYear }
        });
        return response.data.data;
    });

    // Fetch expenses by category
    const { data: expensesByCategory } = useQuery(['online-expenses-by-category', dateRange], async () => {
        const params = {};
        if (dateRange && dateRange[0] && dateRange[1]) {
            params.startDate = dateRange[0].toISOString();
            params.endDate = dateRange[1].toISOString();
        }
        const response = await api.get('/online-reports/expenses-by-category', { params });
        return response.data.data;
    });

    // Fetch profit/loss
    const { data: profitLossData } = useQuery(['online-profit-loss', dateRange], async () => {
        const params = {};
        if (dateRange && dateRange[0] && dateRange[1]) {
            params.startDate = dateRange[0].toISOString();
            params.endDate = dateRange[1].toISOString();
        }
        const response = await api.get('/online-reports/profit-loss', { params });
        return response.data.data;
    });

    const totalIncomeUSD = summaryData?.income?.find(i => i._id === 'USD')?.total || 0;
    const totalIncomeIQD = summaryData?.income?.find(i => i._id === 'IQD')?.total || 0;
    const totalExpenseUSD = summaryData?.expenses?.find(e => e._id === 'USD')?.total || 0;
    const totalExpenseIQD = summaryData?.expenses?.find(e => e._id === 'IQD')?.total || 0;

    const profitUSD = profitLossData?.USD?.profit || 0;
    const profitIQD = profitLossData?.IQD?.profit || 0;

    const paymentStatusData = summaryData?.paymentStatus || [];

    return (
        <div>
            <h1>{t('onlineAccounting.reports')}</h1>

            <Space style={{ marginBottom: 16 }}>
                <RangePicker onChange={setDateRange} />
                <Select value={selectedYear} onChange={setSelectedYear} style={{ width: 120 }}>
                    <Option value={2024}>2024</Option>
                    <Option value={2025}>2025</Option>
                    <Option value={2026}>2026</Option>
                </Select>
            </Space>

            {/* Summary Statistics */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title={t('onlineAccounting.totalStudents')}
                            value={summaryData?.students?.total || 0}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title={t('onlineAccounting.activeStudents')}
                            value={summaryData?.students?.active || 0}
                            valueStyle={{ color: '#3f8600' }}
                            prefix={<ArrowUpOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title={t('onlineAccounting.totalIncomeUSD')}
                            value={totalIncomeUSD}
                            precision={2}
                            valueStyle={{ color: '#3f8600' }}
                            prefix="$"
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title={t('onlineAccounting.totalExpenseUSD')}
                            value={totalExpenseUSD}
                            precision={2}
                            valueStyle={{ color: '#cf1322' }}
                            prefix="$"
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title={t('onlineAccounting.totalIncomeIQD')}
                            value={totalIncomeIQD}
                            precision={0}
                            valueStyle={{ color: '#3f8600' }}
                            suffix="IQD"
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title={t('onlineAccounting.totalExpenseIQD')}
                            value={totalExpenseIQD}
                            precision={0}
                            valueStyle={{ color: '#cf1322' }}
                            suffix="IQD"
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title={t('onlineAccounting.profitUSD')}
                            value={profitUSD}
                            precision={2}
                            valueStyle={{ color: profitUSD >= 0 ? '#3f8600' : '#cf1322' }}
                            prefix={profitUSD >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                            suffix="$"
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title={t('onlineAccounting.profitIQD')}
                            value={profitIQD}
                            precision={0}
                            valueStyle={{ color: profitIQD >= 0 ? '#3f8600' : '#cf1322' }}
                            prefix={profitIQD >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                            suffix="IQD"
                        />
                    </Card>
                </Col>
            </Row>

            {/* Payment Status */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={24}>
                    <Card title={t('onlineAccounting.studentPaymentStatus')}>
                        <Row gutter={16}>
                            {paymentStatusData.map(status => (
                                <Col key={status._id} span={8}>
                                    <Statistic
                                        title={t(`onlineAccounting.${status._id}`)}
                                        value={status.count}
                                    />
                                </Col>
                            ))}
                        </Row>
                    </Card>
                </Col>
            </Row>

            {/* Monthly Income Table */}
            <Row gutter={16}>
                <Col span={12}>
                    <Card title={t('onlineAccounting.monthlyIncome')}>
                        <Table
                            dataSource={incomeByMonth}
                            rowKey={(record) => `${record._id.month}-${record._id.currency}`}
                            pagination={false}
                            size="small"
                            columns={[
                                {
                                    title: t('onlineAccounting.month'),
                                    dataIndex: '_id',
                                    key: 'month',
                                    render: (id) => `${t('month')} ${id.month}`
                                },
                                {
                                    title: t('onlineAccounting.currency'),
                                    dataIndex: '_id',
                                    key: 'currency',
                                    render: (id) => id.currency
                                },
                                {
                                    title: t('onlineAccounting.amount'),
                                    dataIndex: 'total',
                                    key: 'total',
                                    render: (total) => total?.toLocaleString()
                                },
                                {
                                    title: t('onlineAccounting.totalPayments'),
                                    dataIndex: 'count',
                                    key: 'count'
                                }
                            ]}
                        />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card title={t('onlineAccounting.expensesByCategory')}>
                        <Table
                            dataSource={expensesByCategory}
                            rowKey={(record) => `${record._id.category}-${record._id.currency}`}
                            pagination={false}
                            size="small"
                            columns={[
                                {
                                    title: t('onlineAccounting.category'),
                                    dataIndex: '_id',
                                    key: 'category',
                                    render: (id) => t(`onlineAccounting.${id.category}`)
                                },
                                {
                                    title: t('onlineAccounting.currency'),
                                    dataIndex: '_id',
                                    key: 'currency',
                                    render: (id) => id.currency
                                },
                                {
                                    title: t('onlineAccounting.amount'),
                                    dataIndex: 'total',
                                    key: 'total',
                                    render: (total) => total?.toLocaleString()
                                },
                                {
                                    title: t('onlineAccounting.count'),
                                    dataIndex: 'count',
                                    key: 'count'
                                }
                            ]}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default OnlineReports;
